import React, { useEffect, useState } from 'react';
import { fastapiService } from '../../services/fastapiService';

export const WorkflowsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [runs, setRuns] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const totalPages = Math.max(1, Math.ceil(total / limit));

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        const [wf, wr] = await Promise.all([
          fastapiService.getWorkflows({ page, limit }),
          fastapiService.getWorkflowRuns({ page: 1, limit: 10 }),
        ]);
        setWorkflows(wf.data || []);
        setTotal(wf.pagination?.total || 0);
        setRuns(wr.data || []);
      } catch (e: any) {
        setError(e?.message || 'Failed to load workflows');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [page, limit]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-admin-900">Workflows</h1>
        <p className="mt-1 text-sm text-admin-600">
          Monitor and manage workflows across all workspaces
        </p>
      </div>
      
      <div className="admin-card p-6 space-y-6">
        {error && (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}
        {loading ? (
          <p className="text-admin-500">Loading...</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-admin-200">
                <thead>
                  <tr className="text-left text-xs font-medium uppercase tracking-wider text-admin-500">
                    <th className="px-3 py-2">Workflow</th>
                    <th className="px-3 py-2">Workspace</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-admin-200 text-sm">
                  {workflows.map((w: any) => (
                    <tr key={w.id}>
                      <td className="px-3 py-2">{w.name || '-'}</td>
                      <td className="px-3 py-2">{w.workspaces?.name || w.workspace_id || '-'}</td>
                      <td className="px-3 py-2">{w.status || '-'}</td>
                      <td className="px-3 py-2">{w.created_at ? new Date(w.created_at).toLocaleString() : '-'}</td>
                    </tr>
                  ))}
                  {workflows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-3 py-8 text-center text-admin-500">No workflows found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm text-admin-500">Page {page} of {totalPages} • {total} total</div>
              <div className="flex items-center gap-2">
                <button className="admin-button h-8 px-3" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>Prev</button>
                <button className="admin-button h-8 px-3" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Next</button>
              </div>
            </div>

            <div>
              <h2 className="mb-3 text-sm font-semibold text-admin-800">Recent Runs</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-admin-200">
                  <thead>
                    <tr className="text-left text-xs font-medium uppercase tracking-wider text-admin-500">
                      <th className="px-3 py-2">Workflow</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Started</th>
                      <th className="px-3 py-2">Finished</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-admin-200 text-sm">
                    {runs.map((r: any) => (
                      <tr key={r.id}>
                        <td className="px-3 py-2">{r.workflows?.name || r.workflow_id || '-'}</td>
                        <td className="px-3 py-2">{r.status || '-'}</td>
                        <td className="px-3 py-2">{r.started_at ? new Date(r.started_at).toLocaleString() : '-'}</td>
                        <td className="px-3 py-2">{r.finished_at ? new Date(r.finished_at).toLocaleString() : '-'}</td>
                      </tr>
                    ))}
                    {runs.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-3 py-8 text-center text-admin-500">No runs found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

